// LineAdjustmentsEditor — a PO line's manual adjustments ledger: an opening/
// existing balance (positive) or a SAP true-up (either sign), which flow into
// the line's consumed budget alongside its invoiced splits. Presentational
// leaf: it renders the draft list and reports edits via onChange; the parent
// (PoLineEditor) owns persistence. Collapsed by default so a line with no
// adjustments stays compact — the header shows the running net total even
// while collapsed so it's visible without expanding.
//
// The amount field is CurrencyInput with showSymbol={false}: a "$" prefix to
// the left of the raw text would make a negative value read as "$-500.00"
// instead of the conventional "-$500.00" — confusing for a field whose sign is
// the primary signal (opening balance vs. true-up). Dropping the symbol keeps
// the sign unambiguous while retaining grouped 2dp display and comma-tolerant
// input; parseMoney/formatAud handle the signed value everywhere else the same
// as any other money field.

import { useState } from "react";
import { ChevronRight, Plus, Trash2 } from "lucide-react";
import { clsx } from "clsx";
import CurrencyInput from "../../common/CurrencyInput";
import { formatAud } from "../../../utils/financeCalc";
import {
  makeEmptyAdjustment,
  parseMoney,
  type LineAdjustmentDraft,
} from "./poFormState";

interface LineAdjustmentsEditorProps {
  adjustments: LineAdjustmentDraft[];
  onChange: (next: LineAdjustmentDraft[]) => void;
  disabled?: boolean;
}

function fieldId(rowKey: string, field: string): string {
  return `${rowKey}-${field}`;
}

function netTotal(adjustments: LineAdjustmentDraft[]): number {
  return adjustments.reduce((sum, a) => sum + (parseMoney(a.amount) ?? 0), 0);
}

export default function LineAdjustmentsEditor({
  adjustments,
  onChange,
  disabled,
}: LineAdjustmentsEditorProps) {
  const [expanded, setExpanded] = useState(false);

  const patchRow = (key: string, patch: Partial<LineAdjustmentDraft>) => {
    onChange(
      adjustments.map((a) => (a.key === key ? { ...a, ...patch } : a)),
    );
  };

  const removeRow = (key: string) => {
    onChange(adjustments.filter((a) => a.key !== key));
  };

  const addRow = () => {
    onChange([...adjustments, makeEmptyAdjustment()]);
  };

  const total = netTotal(adjustments);

  return (
    <div className="rounded-md border border-slate-200 bg-white p-2 dark:border-slate-700 dark:bg-slate-800">
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          aria-label={`${expanded ? "Collapse" : "Expand"} adjustments`}
          aria-expanded={expanded}
          onClick={() => setExpanded(!expanded)}
          className="inline-flex items-center gap-1 rounded-sm text-caption font-medium text-slate-600 hover:text-slate-900 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:text-slate-300 dark:hover:text-slate-100"
        >
          <ChevronRight
            className={clsx(
              "h-3.5 w-3.5 shrink-0 transition-transform",
              expanded && "rotate-90",
            )}
            aria-hidden="true"
          />
          Adjustments
          {adjustments.length > 0 && (
            <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-slate-600 dark:bg-slate-700 dark:text-slate-300">
              {adjustments.length}
            </span>
          )}
        </button>
        <span
          data-testid="adjustments-net-total"
          className="text-caption font-medium tabular-nums text-slate-600 dark:text-slate-300"
        >
          Net adjustment: {formatAud(total)}
        </span>
      </div>

      {expanded && (
        <div className="mt-2 space-y-2">
          {adjustments.map((adjustment) => {
            const id = (field: string) => fieldId(adjustment.key, field);
            return (
              <div
                key={adjustment.key}
                data-testid="adjustment-row"
                className="grid grid-cols-2 gap-2 rounded-md border border-slate-200 bg-slate-50/60 p-2 sm:grid-cols-12 dark:border-slate-700 dark:bg-slate-900/40"
              >
                <div className="sm:col-span-2">
                  <label className="label" htmlFor={id("amount")}>
                    Amount
                  </label>
                  <CurrencyInput
                    id={id("amount")}
                    size="sm"
                    ariaLabel="Adjustment amount"
                    className="w-full"
                    placeholder="-500 or 2000"
                    showSymbol={false}
                    value={adjustment.amount}
                    onChange={(raw) =>
                      patchRow(adjustment.key, { amount: raw })
                    }
                    disabled={disabled}
                  />
                </div>
                <div className="sm:col-span-4">
                  <label className="label" htmlFor={id("label")}>
                    Label
                  </label>
                  <input
                    id={id("label")}
                    type="text"
                    aria-label="Adjustment label"
                    className="input-sm w-full"
                    placeholder="Opening balance"
                    value={adjustment.label}
                    onChange={(e) =>
                      patchRow(adjustment.key, { label: e.target.value })
                    }
                    disabled={disabled}
                  />
                </div>
                <div className="sm:col-span-4">
                  <label className="label" htmlFor={id("note")}>
                    Note (optional)
                  </label>
                  <input
                    id={id("note")}
                    type="text"
                    aria-label="Adjustment note"
                    className="input-sm w-full"
                    value={adjustment.note}
                    onChange={(e) =>
                      patchRow(adjustment.key, { note: e.target.value })
                    }
                    disabled={disabled}
                  />
                </div>
                <div className="sm:col-span-1">
                  <label className="label" htmlFor={id("date")}>
                    Date
                  </label>
                  <input
                    id={id("date")}
                    type="date"
                    aria-label="Adjustment date"
                    className="input-sm w-full"
                    value={adjustment.effectiveDate}
                    onChange={(e) =>
                      patchRow(adjustment.key, {
                        effectiveDate: e.target.value,
                      })
                    }
                    disabled={disabled}
                  />
                </div>
                <div className="flex items-end justify-end sm:col-span-1">
                  {!disabled && (
                    <button
                      type="button"
                      aria-label="Remove adjustment"
                      onClick={() => removeRow(adjustment.key)}
                      className="btn-icon-ghost btn-icon-sm text-slate-500 hover:text-red-600 dark:text-slate-400 dark:hover:text-red-400"
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          {!disabled && (
            <button
              type="button"
              onClick={addRow}
              className="btn-ghost inline-flex items-center gap-1 px-2 py-1 text-caption"
            >
              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
              Add adjustment
            </button>
          )}
        </div>
      )}
    </div>
  );
}
